<template>
  <div
    ref="root"
    class="china-province-map"
    :class="{ 'is-zoomed': isMoved, 'is-panning': panning }"
    aria-label="中国省份用户分布地图"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dblclick="onDblClick"
  >
    <div class="map-scene" :style="sceneStyle">
      <img class="map-art" :src="mapSrc" alt="" />
      <div class="map-hit" aria-hidden="true"></div>
      <div
        v-for="(province, index) in topProvinces"
        :key="province.name"
        class="province-marker"
        :class="{ 'is-hover': hoveredName === province.name }"
        :style="markerStyle(province, index)"
      >
        <div
          class="province-marker-body"
          @mouseenter="hoveredName = province.name"
          @mouseleave="hoveredName = ''"
        >
          <div class="province-tip">
            <b>{{ province.name }}</b>
            <span>活跃用户 {{ formatProvinceCount(province) }}</span>
            <span>收益 ¥{{ formatProvinceRevenue(province) }}</span>
          </div>
          <div class="province-pin" :aria-label="province.name">
            <span class="pin-shadow" aria-hidden="true"></span>
            <div class="pin-spin">
              <img class="pin-icon" :src="pinSrc" alt="" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="map-zoom-bar" @pointerdown.stop>
      <button type="button" title="放大" @click="zoomBy(1.25)">+</button>
      <button
        type="button"
        title="复位"
        :disabled="scale <= 1 && tx === 0 && ty === 0"
        @click="resetView"
      >
        1x
      </button>
      <button type="button" title="缩小" :disabled="scale <= 1" @click="zoomBy(0.8)">
        −
      </button>
    </div>
  </div>
</template>

<script>
const MIN_SCALE = 1
const MAX_SCALE = 3.6
const MIN_VISIBLE = 6
const FULL_VISIBLE_SCALE = 2.5

export default {
  name: "ChinaProvinceMap",
  props: {
    provinces: {
      type: Array,
      default: () => [],
    },
    countFormatter: {
      type: Function,
      default: null,
    },
    revenueFormatter: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      hoveredName: "",
      scale: 1,
      tx: 0,
      ty: 0,
      panning: false,
      panX: 0,
      panY: 0,
    }
  },
  computed: {
    mapSrc() {
      return `${process.env.BASE_URL || "/"}ditu.png?v=6`
    },
    pinSrc() {
      return `${process.env.BASE_URL || "/"}dingwei.png`
    },
    sceneStyle() {
      return {
        "--map-zoom": this.scale,
        transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`,
      }
    },
    isMoved() {
      return this.scale > 1 || this.tx !== 0 || this.ty !== 0 || this.panning
    },
    rankedProvinces() {
      return (this.provinces || []).slice().sort((a, b) => {
        const byUsers =
          (Number(b.userCount) || 0) - (Number(a.userCount) || 0)
        if (byUsers) return byUsers
        return (Number(b.revenue) || 0) - (Number(a.revenue) || 0)
      })
    },
    visibleCount() {
      const total = this.rankedProvinces.length
      const minCount = Math.min(MIN_VISIBLE, total)
      if (total <= minCount || this.scale <= MIN_SCALE) return minCount
      if (this.scale >= FULL_VISIBLE_SCALE) return total
      const progress =
        (this.scale - MIN_SCALE) / (FULL_VISIBLE_SCALE - MIN_SCALE)
      return Math.round(minCount + progress * (total - minCount))
    },
    topProvinces() {
      return this.rankedProvinces.slice(0, this.visibleCount)
    },
  },
  mounted() {
    this.$refs.root.addEventListener("wheel", this.onWheel, { passive: false })
  },
  watch: {
    isMoved(value) {
      this.$emit("zoom-change", value)
    },
    topProvinces(list) {
      const name = this.hoveredName
      if (name && !(list || []).some((item) => item.name === name)) {
        this.hoveredName = ""
      }
    },
  },
  beforeDestroy() {
    if (this.$refs.root) {
      this.$refs.root.removeEventListener("wheel", this.onWheel)
    }
  },
  methods: {
    markerStyle(province, index) {
      return {
        left: `${Number(province.x) || 0}%`,
        top: `${Number(province.y) || 0}%`,
        zIndex:
          this.hoveredName === province.name
            ? 40
            : this.visibleCount - index + 2,
        "--pin-delay": `${(index % 9) * 0.22}s`,
        "--pin-duration": `${1.8 + (index % 5) * 0.18}s`,
      }
    },
    formatProvinceCount(item) {
      return this.countFormatter
        ? this.countFormatter(item.userCount)
        : this.formatCount(item.userCount)
    },
    formatProvinceRevenue(item) {
      return this.revenueFormatter
        ? this.revenueFormatter(item.revenue)
        : this.formatRevenue(item.revenue)
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString("zh-CN")
    },
    formatRevenue(value) {
      return Number(value || 0).toFixed(2)
    },
    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value))
    },
    clampPan() {
      const root = this.$refs.root
      if (!root) return
      const w = root.clientWidth
      const h = root.clientHeight
      const extraX = w * Math.max(this.scale, 1)
      const extraY = h * Math.max(this.scale, 1)
      this.tx = this.clamp(this.tx, -extraX, extraX)
      this.ty = this.clamp(this.ty, -extraY, extraY)
    },
    zoomAt(clientX, clientY, nextScale) {
      const root = this.$refs.root
      if (!root) return
      const scale = this.clamp(nextScale, MIN_SCALE, MAX_SCALE)
      if (scale === this.scale) {
        this.clampPan()
        return
      }
      const rect = root.getBoundingClientRect()
      const cx = clientX - rect.left
      const cy = clientY - rect.top
      const worldX = (cx - this.tx) / this.scale
      const worldY = (cy - this.ty) / this.scale
      this.scale = scale
      this.tx = cx - worldX * scale
      this.ty = cy - worldY * scale
      this.clampPan()
    },
    zoomBy(factor) {
      const root = this.$refs.root
      if (!root) return
      const rect = root.getBoundingClientRect()
      this.zoomAt(
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
        this.scale * factor
      )
    },
    resetView() {
      this.scale = 1
      this.tx = 0
      this.ty = 0
      this.panning = false
      this.$emit("zoom-change", false)
    },
    onWheel(event) {
      event.preventDefault()
      const step = event.deltaY > 0 ? 0.88 : 1.14
      this.zoomAt(event.clientX, event.clientY, this.scale * step)
    },
    onDblClick(event) {
      if (event.target.closest(".map-zoom-bar")) return
      this.zoomAt(event.clientX, event.clientY, this.scale * 1.45)
    },
    onPointerDown(event) {
      if (event.button !== 0) return
      if (event.target.closest(".map-zoom-bar")) return
      if (event.target.closest(".province-marker-body")) return
      this.panning = true
      this.panX = event.clientX
      this.panY = event.clientY
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    onPointerMove(event) {
      if (!this.panning) return
      this.tx += event.clientX - this.panX
      this.ty += event.clientY - this.panY
      this.panX = event.clientX
      this.panY = event.clientY
      this.clampPan()
    },
    onPointerUp() {
      this.panning = false
    },
  },
}
</script>

<style scoped>
.china-province-map {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: visible;
  touch-action: none;
  cursor: grab;
}
.china-province-map.is-zoomed {
  overflow: visible;
  z-index: 8;
}
.china-province-map.is-panning {
  cursor: grabbing;
}
.map-scene {
  position: absolute;
  inset: 0;
  transform-origin: 0 0;
  will-change: transform;
}
.map-art,
.map-hit {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}
.map-art {
  z-index: 0;
  object-fit: contain;
  pointer-events: none;
}
.map-hit {
  z-index: 1;
}
.map-zoom-bar {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 4px;
  pointer-events: auto;
}
.map-zoom-bar button {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid rgba(71, 216, 255, 0.35);
  border-radius: 4px;
  background: rgba(7, 28, 54, 0.72);
  color: #d7f6ff;
  font-size: 15px;
  line-height: 28px;
  cursor: pointer;
}
.map-zoom-bar button:hover:not(:disabled) {
  background: rgba(17, 36, 97, 0.9);
  border-color: rgba(71, 216, 255, 0.7);
}
.map-zoom-bar button:disabled {
  opacity: 0.4;
  cursor: default;
}
.province-marker {
  position: absolute;
  z-index: 2;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
}
.province-marker-body {
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
  transform: translateX(-50%) scale(calc(1 / var(--map-zoom, 1)));
  transform-origin: 50% 100%;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.22s ease;
}
.province-marker.is-hover {
  pointer-events: auto;
}
.province-marker.is-hover .province-marker-body {
  transform: translateX(-50%) scale(calc(1.1 / var(--map-zoom, 1)));
}
.province-marker.is-hover .province-tip {
  background: rgba(17, 36, 97, 0.82);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28);
}
.province-marker.is-hover .pin-icon {
  filter: drop-shadow(0 3px 8px rgba(255, 196, 72, 0.9));
  animation: pin-jump 0.9s ease-in-out infinite;
}
.province-marker.is-hover .pin-shadow {
  animation: pin-shadow 0.9s ease-in-out infinite;
}
.province-pin {
  position: relative;
  width: 22px;
  height: 26px;
  perspective: 48px;
  pointer-events: none;
}
.pin-spin {
  width: 22px;
  height: 26px;
  transform-origin: 50% 88%;
  animation: pin-spin var(--pin-duration, 2s) linear infinite;
  animation-delay: var(--pin-delay, 0s);
  will-change: transform;
  pointer-events: none;
}
.pin-icon {
  position: relative;
  z-index: 1;
  display: block;
  width: 22px;
  height: 26px;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 2px 4px rgba(255, 140, 40, 0.55));
  transform-origin: 50% 88%;
}
.pin-shadow {
  position: absolute;
  left: 50%;
  bottom: 1px;
  z-index: 0;
  width: 10px;
  height: 4px;
  margin-left: -5px;
  border-radius: 50%;
  background: rgba(18, 10, 4, 0.38);
  filter: blur(1px);
  pointer-events: none;
}
@keyframes pin-spin {
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(360deg);
  }
}
@keyframes pin-jump {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(-8px) scale(1.08);
  }
}
@keyframes pin-shadow {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.42;
  }
  40% {
    transform: scale(0.55);
    opacity: 0.22;
  }
}
.province-tip {
  position: relative;
  z-index: 2;
  min-width: 88px;
  margin-bottom: 4px;
  padding: 6px 8px;
  border-radius: 4px;
  background: rgba(17, 36, 97, 0.6);
  color: #f4f4f8;
  font-size: 11px;
  line-height: 1.45;
  text-align: center;
  white-space: nowrap;
  pointer-events: auto;
}
.province-tip b {
  display: block;
  color: #fff;
  font-size: 12px;
}
.province-tip span {
  display: block;
}
</style>
